/**
 * Ward choropleth map — geographic product sales.
 *
 * Renders municipal ward polygons (from `/api/bi/wards/`) coloured on a
 * quantile ramp of the selected sales metric (from
 * `/api/bi/products/wards/`). A floating layer panel provides the ward
 * polygon toggle plus ward labels, a centroid "bubble" alternative, and
 * store geofence/marker overlays. Hovering highlights a ward with a
 * metric tooltip; clicking selects it so the page can open the drill
 * panel. Server geometry is GeoJSON `[lng, lat]`; Leaflet wants
 * `[lat, lng]`, so every ring is converted here.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  CircleMarker,
  MapContainer,
  Marker,
  Polygon,
  Tooltip,
  TileLayer,
  useMap,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import type {
  StoreGeofenceStore,
  WardFeature,
  WardSalesRow,
  WardsGeoResponse,
} from '@/api/bi/types';
import { fmt } from '@/lib/format';

const KTM: L.LatLngTuple = [27.7172, 85.324];

export type WardMetric = 'gmv' | 'units' | 'orders' | 'customers' | 'aov' | 'margin';

export const WARD_METRIC_LABELS: Record<WardMetric, string> = {
  gmv: 'GMV',
  units: 'Units',
  orders: 'Orders',
  customers: 'Customers',
  aov: 'AOV',
  margin: 'Margin',
};

const MONEY_METRICS: ReadonlySet<WardMetric> = new Set(['gmv', 'aov', 'margin']);

export function formatWardMetric(metric: WardMetric, value: number): string {
  return MONEY_METRICS.has(metric) ? fmt.npr(value) : fmt.number(value);
}

/** Sequential rose ramp (light → dark) aligned with the brand palette. */
const RAMP = ['#fecdd3', '#fda4af', '#fb7185', '#e11d48', '#9f1239'];
const NO_DATA_FILL = '#94a3b8';

interface ChoroplethScale {
  /** Ascending quantile thresholds; values <= thresholds[i] fall in bin i. */
  thresholds: number[];
  colorFor: (value: number | null | undefined) => string;
  bins: Array<{ color: string; label: string }>;
}

function buildScale(values: number[], metric: WardMetric): ChoroplethScale {
  const positive = values.filter((v) => v > 0).sort((a, b) => a - b);
  if (positive.length === 0) {
    return {
      thresholds: [],
      colorFor: () => NO_DATA_FILL,
      bins: [],
    };
  }
  const q = (p: number) => positive[Math.min(positive.length - 1, Math.floor(p * positive.length))];
  // Quantile bins keep sparse data differentiated (linear bins would put
  // everything in the bottom bucket when one ward dominates).
  const thresholds = [q(0.2), q(0.4), q(0.6), q(0.8), positive[positive.length - 1]];
  const colorFor = (value: number | null | undefined) => {
    if (value == null || value <= 0) return NO_DATA_FILL;
    for (let i = 0; i < thresholds.length; i += 1) {
      if (value <= thresholds[i]) return RAMP[i];
    }
    return RAMP[RAMP.length - 1];
  };
  const bins: Array<{ color: string; label: string }> = [];
  let lo = 0;
  for (let i = 0; i < thresholds.length; i += 1) {
    // Merge duplicate quantiles (tiny datasets) into one legend row.
    if (i > 0 && thresholds[i] === thresholds[i - 1]) continue;
    bins.push({
      color: RAMP[i],
      label: `${formatWardMetric(metric, lo)} – ${formatWardMetric(metric, thresholds[i])}`,
    });
    lo = thresholds[i];
  }
  return { thresholds, colorFor, bins };
}

/** Convert GeoJSON Polygon/MultiPolygon coordinates to Leaflet rings. */
function featureRings(feature: WardFeature): L.LatLngTuple[][] {
  const geom = feature.geometry;
  const toRing = (ring: number[][]): L.LatLngTuple[] =>
    ring.map(([lng, lat]) => [lat, lng] as L.LatLngTuple);
  if (geom.type === 'Polygon') {
    return (geom.coordinates as number[][][]).map(toRing);
  }
  // MultiPolygon: flatten each polygon's rings; Leaflet accepts the array.
  const out: L.LatLngTuple[][] = [];
  for (const poly of geom.coordinates as number[][][][]) {
    for (const ring of poly) out.push(toRing(ring));
  }
  return out;
}

function wardLabelIcon(text: string, muted: boolean): L.DivIcon {
  return L.divIcon({
    className: 'ward-label',
    html: `<span style="
      display:inline-block;padding:1px 5px;border-radius:5px;
      background:${muted ? 'rgba(255,255,255,0.72)' : 'rgba(255,255,255,0.92)'};
      color:#0f172a;font-size:10px;font-weight:700;white-space:nowrap;
      box-shadow:0 1px 2px rgba(0,0,0,.25);pointer-events:none;
    ">${text}</span>`,
    iconSize: [0, 0],
    iconAnchor: [0, 0],
  });
}

function storePinIcon(): L.DivIcon {
  return L.divIcon({
    className: 'ward-map-store',
    html: `<span style="
      display:inline-flex;align-items:center;justify-content:center;
      width:20px;height:20px;border-radius:6px;
      background:#0f172a;color:white;font-size:11px;font-weight:700;
      box-shadow:0 0 0 2px white, 0 1px 3px rgba(0,0,0,.4);
    ">S</span>`,
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });
}

function FitToWards({ features }: { features: WardFeature[] }) {
  const map = useMap();
  useEffect(() => {
    const pts: L.LatLngTuple[] = [];
    for (const f of features) {
      const c = f.properties.centroid;
      if (c && Number.isFinite(c[0]) && Number.isFinite(c[1])) pts.push([c[1], c[0]]);
    }
    if (pts.length === 0) return;
    if (pts.length === 1) {
      map.setView(pts[0], 13);
      return;
    }
    map.fitBounds(L.latLngBounds(pts), { padding: [24, 24], maxZoom: 14 });
    // Refit only when the ward footprint changes, not on hover/selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [features.length]);
  return null;
}

interface LayerVisibility {
  wardPolygons: boolean;
  wardLabels: boolean;
  bubbles: boolean;
  storeGeofences: boolean;
  storeMarkers: boolean;
}

const LAYER_LABELS: Array<{ key: keyof LayerVisibility; label: string }> = [
  { key: 'wardPolygons', label: 'Ward polygons' },
  { key: 'wardLabels', label: 'Ward labels' },
  { key: 'bubbles', label: 'Sales bubbles' },
  { key: 'storeGeofences', label: 'Store geofences' },
  { key: 'storeMarkers', label: 'Store markers' },
];

function MapControls({
  layers,
  setLayers,
  metric,
  scale,
}: {
  layers: LayerVisibility;
  setLayers: (next: LayerVisibility) => void;
  metric: WardMetric;
  scale: ChoroplethScale;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (ref.current) {
      L.DomEvent.disableClickPropagation(ref.current);
      L.DomEvent.disableScrollPropagation(ref.current);
    }
  }, []);

  return (
    <div className="leaflet-bottom leaflet-left">
      <div
        ref={ref}
        className="leaflet-control"
        style={{
          margin: '12px',
          padding: '8px 10px',
          borderRadius: '10px',
          background: 'rgba(255,255,255,0.96)',
          boxShadow: '0 1px 6px rgba(0,0,0,.25)',
          fontSize: '11px',
          color: '#0f172a',
          minWidth: '150px',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
          {LAYER_LABELS.map(({ key, label }) => (
            <label
              key={key}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}
            >
              <input
                type="checkbox"
                checked={layers[key]}
                onChange={(e) => setLayers({ ...layers, [key]: e.target.checked })}
                style={{ accentColor: '#e11d48' }}
              />
              <span>{label}</span>
            </label>
          ))}
        </div>

        <div style={{ marginTop: '8px', borderTop: '1px solid #e2e8f0', paddingTop: '6px' }}>
          <div style={{ fontWeight: 700, marginBottom: '3px', color: '#475569' }}>
            {WARD_METRIC_LABELS[metric]} per ward
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {scale.bins.length === 0 ? (
              <span style={{ color: '#64748b' }}>No sales in range</span>
            ) : (
              <>
                {scale.bins.map((b) => (
                  <div key={b.color} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span
                      style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '2px',
                        background: b.color,
                        display: 'inline-block',
                        flexShrink: 0,
                      }}
                    />
                    <span style={{ color: '#64748b', whiteSpace: 'nowrap' }}>{b.label}</span>
                  </div>
                ))}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span
                    style={{
                      width: '10px',
                      height: '10px',
                      borderRadius: '2px',
                      background: NO_DATA_FILL,
                      display: 'inline-block',
                      opacity: 0.5,
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ color: '#64748b' }}>No sales</span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export interface WardChoroplethMapProps {
  wardsGeo: WardsGeoResponse | undefined;
  sales: WardSalesRow[];
  metric: WardMetric;
  compared?: boolean;
  selectedWardId: number | null;
  onWardSelect: (id: number | null) => void;
  /** External cross-highlight (e.g. hovering a row in the ranked table). */
  highlightWardId?: number | null;
  onWardHover?: (id: number | null) => void;
  storeGeofences?: StoreGeofenceStore[];
  /** Hide the floating layer panel (used by the compact embed). */
  compact?: boolean;
  height?: number | string;
  className?: string;
}

export function WardChoroplethMap({
  wardsGeo,
  sales,
  metric,
  compared = false,
  selectedWardId,
  onWardSelect,
  highlightWardId = null,
  onWardHover,
  storeGeofences = [],
  compact = false,
  height = 560,
  className,
}: WardChoroplethMapProps) {
  const [layers, setLayers] = useState<LayerVisibility>({
    wardPolygons: true,
    wardLabels: false,
    bubbles: false,
    storeGeofences: false,
    storeMarkers: true,
  });
  const [hoveredId, setHoveredId] = useState<number | null>(null);

  const features = wardsGeo?.features ?? [];
  const salesByWard = useMemo(() => {
    const m = new Map<number, WardSalesRow>();
    for (const row of sales) m.set(row.id, row);
    return m;
  }, [sales]);

  const scale = useMemo(
    () => buildScale(sales.map((r) => r[metric]), metric),
    [sales, metric],
  );

  const maxMetric = useMemo(
    () => sales.reduce((m, r) => Math.max(m, r[metric]), 0) || 1,
    [sales, metric],
  );

  const activeId = highlightWardId ?? hoveredId;

  const handleHover = (id: number | null) => {
    setHoveredId(id);
    onWardHover?.(id);
  };

  const wardTooltip = (feature: WardFeature, row: WardSalesRow | undefined) => (
    <div className="text-xs">
      <div className="font-semibold text-slate-900">{feature.properties.ward_label}</div>
      <div className="text-[11px] text-slate-500">{feature.properties.municipality}</div>
      {row ? (
        <div className="mt-1 space-y-0.5">
          <div>
            <span className="text-slate-500">{WARD_METRIC_LABELS[metric]} · </span>
            <span className="font-semibold text-slate-900">
              {formatWardMetric(metric, row[metric])}
            </span>
          </div>
          <div className="text-[11px] text-slate-500">
            {fmt.number(row.orders)} orders · {fmt.number(row.units)} units ·{' '}
            {fmt.pct(row.share_pct)} of GMV
          </div>
          {compared && row.gmv_delta_pct != null ? (
            <div
              className={`text-[11px] font-semibold ${
                row.gmv_delta_pct >= 0 ? 'text-green-600' : 'text-red-600'
              }`}
            >
              GMV {row.gmv_delta_pct >= 0 ? '+' : ''}
              {fmt.pct(row.gmv_delta_pct)} vs compare
            </div>
          ) : null}
        </div>
      ) : (
        <div className="mt-1 text-[11px] text-slate-500">No sales in range</div>
      )}
    </div>
  );

  return (
    <div
      className={className}
      style={{ height, position: 'relative', isolation: 'isolate', zIndex: 0 }}
    >
      <MapContainer
        center={KTM}
        zoom={12}
        scrollWheelZoom
        style={{
          height: '100%',
          width: '100%',
          borderRadius: 'inherit',
          position: 'relative',
          zIndex: 0,
        }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitToWards features={features} />
        {!compact ? (
          <MapControls layers={layers} setLayers={setLayers} metric={metric} scale={scale} />
        ) : null}

        {/* Ward choropleth polygons */}
        {layers.wardPolygons &&
          features.map((f) => {
            const row = salesByWard.get(f.properties.id);
            const isActive = activeId === f.properties.id;
            const isSelected = selectedWardId === f.properties.id;
            const fill = scale.colorFor(row?.[metric]);
            return (
              <Polygon
                key={`ward-${f.properties.id}`}
                positions={featureRings(f)}
                pathOptions={{
                  color: isSelected ? '#0f172a' : isActive ? '#334155' : '#64748b',
                  weight: isSelected ? 2.5 : isActive ? 2 : 0.8,
                  fillColor: fill,
                  fillOpacity: row ? (isActive || isSelected ? 0.75 : 0.55) : 0.12,
                }}
                eventHandlers={{
                  click: () =>
                    onWardSelect(selectedWardId === f.properties.id ? null : f.properties.id),
                  mouseover: () => handleHover(f.properties.id),
                  mouseout: () => handleHover(null),
                }}
              >
                <Tooltip sticky direction="top">
                  {wardTooltip(f, row)}
                </Tooltip>
              </Polygon>
            );
          })}

        {/* Ward number labels at centroids */}
        {layers.wardLabels &&
          features.map((f) => {
            const c = f.properties.centroid;
            if (!c) return null;
            const row = salesByWard.get(f.properties.id);
            return (
              <Marker
                key={`label-${f.properties.id}`}
                position={[c[1], c[0]]}
                icon={wardLabelIcon(f.properties.ward_label, !row)}
                interactive={false}
                zIndexOffset={200}
              />
            );
          })}

        {/* Bubble alternative: metric-sized circles at ward centroids */}
        {layers.bubbles &&
          sales.map((row) => {
            if (!row.centroid) return null;
            const r = 6 + Math.sqrt(row[metric] / maxMetric) * 22;
            const isSelected = selectedWardId === row.id;
            const feature = features.find((f) => f.properties.id === row.id);
            return (
              <CircleMarker
                key={`bubble-${row.id}`}
                center={[row.centroid[1], row.centroid[0]]}
                radius={r}
                pathOptions={{
                  color: isSelected ? '#0f172a' : '#be123c',
                  weight: isSelected ? 2.5 : 1,
                  fillColor: '#e11d48',
                  fillOpacity: 0.4,
                }}
                eventHandlers={{
                  click: () => onWardSelect(selectedWardId === row.id ? null : row.id),
                  mouseover: () => handleHover(row.id),
                  mouseout: () => handleHover(null),
                }}
              >
                <Tooltip sticky direction="top">
                  {feature ? (
                    wardTooltip(feature, row)
                  ) : (
                    <div className="text-xs">
                      <div className="font-semibold">{row.ward_label}</div>
                      <div>{formatWardMetric(metric, row[metric])}</div>
                    </div>
                  )}
                </Tooltip>
              </CircleMarker>
            );
          })}

        {/* Store geofence outlines */}
        {layers.storeGeofences &&
          storeGeofences.map((s) =>
            s.geofences.map((gf) =>
              gf.rings.map((ring, idx) => (
                <Polygon
                  key={`gf-${s.id}-${gf.id}-${idx}`}
                  positions={ring.map(([lng, lat]) => [lat, lng] as L.LatLngTuple)}
                  pathOptions={{
                    color: gf.is_primary ? '#2563eb' : '#0ea5e9',
                    weight: 1.2,
                    fill: false,
                    dashArray: gf.is_primary ? undefined : '4 4',
                  }}
                >
                  <Tooltip sticky direction="top" className="text-xs">
                    {s.name}
                    {gf.is_primary ? '' : ' (secondary)'}
                  </Tooltip>
                </Polygon>
              )),
            ),
          )}

        {/* Store markers */}
        {layers.storeMarkers &&
          storeGeofences.map((s) =>
            Number.isFinite(s.latitude) && Number.isFinite(s.longitude) ? (
              <Marker
                key={`store-${s.id}`}
                position={[s.latitude, s.longitude]}
                icon={storePinIcon()}
                zIndexOffset={500}
              >
                <Tooltip direction="top">{s.name}</Tooltip>
              </Marker>
            ) : null,
          )}
      </MapContainer>
    </div>
  );
}
